import { createStore } from 'vuex';
import axios from 'axios';

const API_AUTHENTICATION_URL = process.env.VUE_APP_API_URL + "/authentication";
const ACCESS_TOKEN_NAME_IN_STORAGE = process.env.VUE_APP_ACCESS_TOKEN_NAME_IN_STORAGE;

export default createStore({
  state: {
    user: null,
    refresh: Date.now(),
    alert: {
      title: '',
      message: '',
      type: '',
      showAlert: false
    },
    theme: localStorage.getItem('theme') || 'light',
    lastEvaluationDeadlineId: null,
  },
  mutations: {
    setLastEvaluationDeadlineId(state, deadlineId) {
      state.lastEvaluationDeadlineId = deadlineId;
    },
    setUser(state, user) {
      state.user = user;
      axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem(ACCESS_TOKEN_NAME_IN_STORAGE)}`;
    },
    clearUser(state) {
      state.user = null;
      delete axios.defaults.headers.common['Authorization'];
    },
    showAlert(state, { title, message, type }) {
      state.alert = {
        title,
        message,
        type,
        showAlert: true
      };
    },
    hideAlert(state) {
      state.alert.showAlert = false;
    },
    updateRefresh(state) {
      state.refresh = Date.now();
    },
    setTheme(state, theme) {
      state.theme = theme;
    },
  },
  actions: {
    toggleTheme({ commit, state }) {
      const newTheme = state.theme === "light" ? "dark" : "light";
      localStorage.setItem("theme", newTheme);
      commit("setTheme", newTheme);
      document.documentElement.setAttribute("data-bs-theme", newTheme);
    },
    async fetchUser({ commit }) {
      try {
        const token = localStorage.getItem(ACCESS_TOKEN_NAME_IN_STORAGE);
        if (token) {
          const response = await axios.get(`${API_AUTHENTICATION_URL}/get_token_data`, {
            headers: {
              Authorization: `Bearer ${token}`,
            }
          });
          commit('clearUser');
          commit('setUser', response.data);
        } else {
          commit('clearUser');
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        commit('clearUser');
      }
    },
    async refreshToken({ commit, dispatch }) {
      try {
        const token = localStorage.getItem(ACCESS_TOKEN_NAME_IN_STORAGE);
        if (token) {
          const response = await axios.post(`${API_AUTHENTICATION_URL}/refresh_token`, { token });
          // Update local storage with the new token.
          const { access_token } = response.data;
          localStorage.setItem(ACCESS_TOKEN_NAME_IN_STORAGE, access_token);
          
          // Fetch full user data so that the state contains the expected properties
          await dispatch('fetchUser');
        } else {
          commit('clearUser');
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
        commit('clearUser');
      }
    },
    triggerAlert({ commit }, alertData) {
      commit('showAlert', alertData);
      setTimeout(() => {
        commit('hideAlert');
      }, 5000); // 5 seconds
    },
    async impersonateStudent({ dispatch }, studentUco) {
      const response = await axios.post(`${process.env.VUE_APP_API_URL}/authentication/impersonate`, { student_uco: studentUco });
      localStorage.setItem(ACCESS_TOKEN_NAME_IN_STORAGE, response.data.access_token);
      await dispatch('fetchUser');
    },
    async stopImpersonation({ dispatch }) {
      try {
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/authentication/stop_impersonation`);
        const { access_token } = response.data;
        localStorage.setItem(ACCESS_TOKEN_NAME_IN_STORAGE, access_token);
        await dispatch('fetchUser');
      } catch (error) {
        console.error("Stopping impersonation failed", error);
      }
    },
  },
  getters: {
    isTeacher: (state) => state.user?.teacher,
    userId: (state) => state.user?.id,
    userUco: (state) => state.user?.uco,
    semesterId: (state) => state.user?.semester,
    willExpireSoon: (state) => {
      // Use refresh as a dependency so that willExpireSoon is recalculated every minute
      state.refresh;
      if (!state.user || !state.user.exp) {
        return false;
      }
      const expirationDateTime = new Date(state.user.exp * 1000);
      const nowDateTime = new Date();
      const expirationThreshold = nowDateTime.getTime() + (31 * 60 * 1000); // 31 minutes ahead
      return expirationThreshold >= expirationDateTime;
    },
    expireDateTime: (state) => state.user?.exp,
    timeToExpire: (state) => {
      // Use refresh as a dependency so that timeToExpire is recalculated every minute
      state.refresh;
      if (!state.user || !state.user.exp) {
        return 0;
      }
      const expirationDateTime = new Date((state.user.exp * 1000) - (1 * 60 * 1000)); // take 1 minute to expiration times
      const nowDateTime = new Date();
      return expirationDateTime - nowDateTime;
    },
    alert: (state) => state.alert,
    // New getter for theme
    theme: state => state.theme,
    // New getter for impersonation detection by presence of realTeacherId
    isImpersonating: (state) => !!state.user?.realTeacherId,
    lastEvaluationDeadlineId: (state) => state.lastEvaluationDeadlineId,
  },
});